import { Chip, type IconPath } from '@nespresso-design-system/components';
import cn from 'classnames';
import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { trackFilterDropdownOpened } from '@/widgets/ProductList/Tracking.ts';
import styles from './FilterPicker.module.scss';
import type { Filter } from '@/domain/FilteringAndSorting';
import { injectRefToReactComponent } from '@/utils/injectRefToReactComponent';
import { useFocusTrap } from '@/widgets/hooks/useFocusTrap';

export type FilterPickerProps = {
  filter: Filter;
  icon?: IconPath;
  children: ComponentChildren | ((toggle: () => void) => ComponentChildren);
  class?: string;
  isSelected?: boolean;
  filterPickerOpen?: boolean;
  position: 'left' | 'right';
};

/**
 * TODO: The FilterPicker has no bubble and is always placed at the bottom.
 * In the future we could make the Popover Component to support this and
 * directly use the Popover Component here:
 *
 * src/widgets/components/containers/Popover/Popover.tsx
 */
export default function FilterPicker({
  filter,
  icon,
  class: className,
  children,
  isSelected,
  filterPickerOpen = false,
  position
}: FilterPickerProps) {
  const [isOpen, setIsOpen] = useState(filterPickerOpen);
  const filterPickerRef = useRef<HTMLDivElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  const filterId = filter.id;

  useFocusTrap(targetRef, popoverContentRef, isOpen);

  function toggle() {
    setIsOpen(!isOpen);
  }

  function handleResize() {
    setIsOpen(false);
  }

  useEffect(() => {
    function handleEsc(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      if (isOpen && !target.closest(`#${filterId}`)) {
        setIsOpen(false);
      }
    }

    window.addEventListener('keydown', handleEsc);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      trackFilterDropdownOpened(filter);
      window.addEventListener('resize', handleResize);
    } else {
      window.removeEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  function getDropdownContainerClass(
    dropdownIsOpen: boolean,
    dropdownPosition: 'left' | 'right'
  ) {
    if (!dropdownIsOpen) {
      return styles.dropdownContainer;
    }

    return dropdownPosition === 'left'
      ? styles.dropdownContainerVisible
      : styles.dropdownContainerVisibleRight;
  }

  return (
    <div
      class={cn(styles.container, className)}
      id={filter.id}
      ref={filterPickerRef}
    >
      <Chip
        aria-label={filter.label}
        label={filter.label}
        isSelected={isSelected}
        ref={injectRefToReactComponent(targetRef)}
        isActive={isOpen}
        intent="expandable"
        icon={icon}
        onClick={() => setIsOpen(!isOpen)}
      />
      {isOpen && (
        <div
          ref={popoverContentRef}
          class={getDropdownContainerClass(isOpen, position)}
        >
          {typeof children === 'function' ? children(toggle) : children}
        </div>
      )}
    </div>
  );
}
